import { useEffect, useState } from 'react';

import {
  type AssetDelivery,
  type Content,
  contentPath,
  type Delivery,
  fetchDeliveries,
} from './api.ts';
import { type AssetRef, useAssetDeliveries } from './assets.ts';
import { DASHBOARD_POLL_MS, describeError, redirectIfUnauthorised } from './data.tsx';
import { Icon } from './Icon.tsx';
import { Link } from './router.tsx';
import { since } from './schedule.ts';
import { Panel, Skeleton } from './ui.tsx';

/**
 * What the operator has made, as pictures (US-074).
 *
 * The app is a queue everywhere else — statuses, steps, queue positions — and
 * a queue is the wrong shape for looking at work that is finished. Two views
 * read this: the strip on the Overview, and the thumbnails of the content
 * grid. Both draw the poster of the current version, loaded straight from the
 * bucket with a presigned URL (US-055), so nothing on this page passes through
 * the app server.
 */

/**
 * How many tiles the Overview's strip asks for. One row on a wide screen and
 * a short scroll on a narrow one; the rest of the gallery is behind its link.
 */
export const STRIP_LIMIT = 12;

/**
 * How often the list is re-read when nothing the content poll can see has
 * changed.
 *
 * The signature below catches an approval within a poll of it happening, but
 * not the upload that puts the poster in the bucket a moment later — that
 * changes no field of the content row. So there is a slow beat underneath, ten
 * times the dashboard's, for the tile that is waiting for its picture.
 */
const DELIVERIES_POLL_MS = DASHBOARD_POLL_MS * 10;

/**
 * The delivered contents, re-read when the content poll says something was.
 *
 * Driven off the list the whole app already polls rather than polling on its
 * own cadence: an approval, a new version and a deletion all move a field of
 * the content row, so the signature changes and this reads again — no third
 * request every three seconds for a list that changes a few times a day.
 */
export function useDeliveries(contents: readonly Content[] | null): {
  readonly deliveries: readonly Delivery[] | null;
  readonly error: string | null;
} {
  const [deliveries, setDeliveries] = useState<readonly Delivery[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Only the facts that change what the strip shows: which contents are
  // delivered, which version each is at, and when it was approved.
  const signature =
    contents === null
      ? null
      : contents
          .filter((content) => content.status === 'delivered')
          .map(
            (content) =>
              `${content.id}:${String(content.currentVersion)}:${content.approvedAt ?? content.updatedAt}`,
          )
          .join(' ');

  useEffect(() => {
    if (signature === null) return;
    const controller = new AbortController();
    let cancelled = false;
    const read = (): void => {
      fetchDeliveries(STRIP_LIMIT, controller.signal)
        .then((listing) => {
          if (cancelled) return;
          setDeliveries(listing.deliveries);
          setError(null);
        })
        .catch((cause: unknown) => {
          if (cancelled || controller.signal.aborted) return;
          if (redirectIfUnauthorised(cause)) return;
          // The tiles already on screen stay; a strip is not worth emptying.
          setError(describeError(cause));
        });
    };
    read();
    const timer = window.setInterval(read, DELIVERIES_POLL_MS);
    return () => {
      cancelled = true;
      controller.abort();
      window.clearInterval(timer);
    };
  }, [signature]);

  return { deliveries, error };
}

/**
 * Where each delivered content's poster is loaded from, by content id.
 *
 * A delivery with no poster is simply absent: there is no object in the bucket
 * to sign a URL for, and a tile with no picture says so in words rather than
 * drawing an `<img>` that will fail.
 */
export function usePosterUrls(
  deliveries: readonly Delivery[] | null,
): Map<string, AssetDelivery> {
  const refs: AssetRef[] = (deliveries ?? []).flatMap((delivery) =>
    delivery.poster?.assetId == null
      ? []
      : [{ contentId: delivery.contentId, assetId: delivery.poster.assetId }],
  );
  const byAsset = useAssetDeliveries(refs);
  const byContent = new Map<string, AssetDelivery>();
  for (const delivery of deliveries ?? []) {
    const assetId = delivery.poster?.assetId ?? null;
    const url = assetId === null ? undefined : byAsset.get(assetId);
    if (url !== undefined) byContent.set(delivery.contentId, url);
  }
  return byContent;
}

/** The Overview's panel: the last few things that were delivered, as pictures. */
export function DeliveredStrip({ contents }: { readonly contents: readonly Content[] | null }) {
  const { deliveries, error } = useDeliveries(contents);
  const posters = usePosterUrls(deliveries);

  return (
    <Panel
      title="Delivered"
      icon="image"
      actions={
        <Link className="link" href="/content?filter=delivered&view=grid">
          Open the gallery
        </Link>
      }
    >
      {error !== null && <p className="text-danger">Could not read what was delivered: {error}</p>}
      {deliveries === null ? (
        <Skeleton lines={2} />
      ) : deliveries.length === 0 ? (
        <p className="muted">
          Nothing has been approved yet. Approve a version and its poster appears here — this strip
          is the shelf, not the queue.
        </p>
      ) : (
        <ul className="strip">
          {deliveries.map((delivery) => (
            <li className="strip__item" key={delivery.contentId}>
              <Link
                className="strip__tile"
                href={contentPath(delivery.contentId)}
                title={`${delivery.name} · version ${String(delivery.versionNumber)}`}
              >
                <Poster delivery={delivery} url={posters.get(delivery.contentId) ?? null} />
                <span className="strip__name">{delivery.name}</span>
                <span className="strip__meta">
                  v{delivery.versionNumber} · {since(delivery.deliveredAt)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/**
 * One picture, or the reason there is none.
 *
 * The alternative text names the content rather than describing the frame:
 * nothing here knows what is in the picture, and inventing a description of a
 * render is worse than naming the thing it belongs to.
 */
export function Poster({
  delivery,
  url,
  className = 'strip__frame',
}: {
  readonly delivery: Delivery;
  readonly url: AssetDelivery | null;
  readonly className?: string;
}) {
  if (url !== null) {
    return (
      <span className={className}>
        <img
          className="poster__image"
          src={url.url}
          alt={`${delivery.name}, version ${String(delivery.versionNumber)}`}
          loading="lazy"
        />
      </span>
    );
  }
  return (
    <span className={`${className} ${className}--empty`}>
      <Icon name={delivery.kind === 'video' ? 'device-camera-video' : 'image'} />
      <span className="poster__note">
        {delivery.poster === null ? 'not in the bucket yet' : 'no URL for it'}
      </span>
    </span>
  );
}
